import { Area } from '@vestico/utils';

import { WidgetItemAspectRatio } from './widgetItemAspectRatio';

export type Vertex = {
  x: number | null;
  y: number | null;
};

export type BoundingPoly = {
  vertices: Vertex[] | null;
};

export interface DetectedFace {
  confidence: number;
  boundingPoly: BoundingPoly;
  fdBoundingPoly: BoundingPoly;
}

export interface ObjectLocalization {
  mid?: string | null;
  languageCode?: string | null;
  name?: string | null;
  confidence?: number | null;
  boundingPoly: BoundingPoly;
}

export type CropHint = {
  aspectRatio: WidgetItemAspectRatio;
  boundingPoly: BoundingPoly;
  confidence: number;
  importanceFraction: number;
  manual?: boolean;
};

export type CropHints = Partial<Record<WidgetItemAspectRatio, CropHint>>;

export interface Color {
  red: number;
  green: number;
  blue: number;
}

export interface DominantColor {
  color: Color;
  score: number;
  pixelFraction: number;
}

export interface LabelAnnotation {
  mid: string;
  name: string;
  score: number;
  topicality: number;
}

export const LikelihoodOptions = ['UNKNOWN', 'VERY_LIKELY', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'VERY_LIKELY'] as const;
export type Likelihood = (typeof LikelihoodOptions)[number];

export interface SafeSearchAnnotation {
  adult: Likelihood;
  spoof: Likelihood;
  medical: Likelihood;
  violence: Likelihood;
  racy: Likelihood;
}

export const getBoundingPolyRect = (poly: BoundingPoly) => {
  let minX = Number.MAX_VALUE;
  let minY = Number.MAX_VALUE;
  let maxX = -Number.MAX_VALUE;
  let maxY = -Number.MAX_VALUE;

  const { vertices } = poly;
  if (!vertices) {
    return undefined;
  }
  for (const vertex of vertices) {
    const { x, y } = vertex;
    if (typeof x === 'number') {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
    }
    if (typeof y === 'number') {
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }

  if (minX === Number.MAX_SAFE_INTEGER || minY === Number.MAX_SAFE_INTEGER) {
    return undefined;
  }

  const width = maxX - minX;
  const height = maxY - minY;
  const area: Area = {
    x: minX,
    y: minY,
    width,
    height,
  };
  return area;
};

export const calculateArea = (area: Area | undefined) => {
  if (!area) {
    return 0;
  }

  return area.width * area.height;
};

export const getBoundingPolyRectFromArea = (area: Area): BoundingPoly => {
  const { x, y, width, height } = area;
  const vertices = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
  return { vertices };
};
